export const getErrorCode = (payload, fallback = 'UNKNOWN_ERROR') => {
  if (!payload) return fallback;
  if (typeof payload.code === 'string') return payload.code;
  if (typeof payload.detail === 'string') return payload.detail;
  if (payload.detail && typeof payload.detail.code === 'string') return payload.detail.code;
  return fallback;
};

export const requestJson = async (url, options = {}) => {
  const response = await fetch(url, {
    credentials: 'include',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
  });

  let payload = null;
  try {
    payload = await response.json();
  } catch (error) {
    payload = null;
  }

  if (!response.ok) {
    const error = new Error(getErrorCode(payload));
    error.code = getErrorCode(payload);
    error.payload = payload;
    error.status = response.status;
    throw error;
  }

  return payload;
};

// Session of the signed-in user.
//
// /api/auth/me maps onto oauth2-proxy's userinfo, which answers flat
// ({user, email, groups, preferredUsername}) and carries the Keycloak realm and
// client roles as "role:<name>" entries in `groups`. Every page works with
// {user: {username, email, roles}}; the shape is reconciled here, once.
const ROLE_PREFIX = 'role:';

export const normaliseSession = (payload) => {
  if (!payload) return { user: null };
  // Already the object shape, so nothing to reconcile.
  if (payload.user && typeof payload.user === 'object') return payload;

  const username = payload.preferredUsername || payload.preferred_username || payload.email || payload.user;
  if (!username) return { user: null };
  const roles = (Array.isArray(payload.groups) ? payload.groups : [])
    .filter((entry) => typeof entry === 'string')
    .map((entry) => (entry.startsWith(ROLE_PREFIX) ? entry.slice(ROLE_PREFIX.length) : entry));

  return {
    ...payload,
    user: { username, email: payload.email || '', roles },
  };
};

export const fetchCurrentUser = async () =>
  normaliseSession(await requestJson('/api/auth/me', { method: 'GET' }));

// Sign-in, sign-out and the account console belong to Keycloak behind
// oauth2-proxy; the homepage only links there.
export const KEYCLOAK_ACCOUNT_CONSOLE = '/keycloak/realms/paioneer/account';

export const signInUrl = (returnTo = window.location.href) =>
  `/oauth2/start?rd=${encodeURIComponent(returnTo)}`;

// Sign-out ends both sessions: oauth2-proxy drops its cookie and ends the
// Keycloak session server-side with the session's ID token (its
// backend-logout-url), so the next sign-in asks for the password again.
export const signOutUrl = (returnTo = '/') => `/oauth2/sign_out?rd=${encodeURIComponent(returnTo)}`;

export const requestForm = async (url, values, options = {}) => {
  const body = new URLSearchParams();
  Object.entries(values || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      body.append(key, value);
    }
  });

  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    ...options,
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      ...(options.headers || {}),
    },
    body: body.toString(),
  });

  let payload = null;
  try {
    payload = await response.json();
  } catch (error) {
    payload = null;
  }

  if (!response.ok) {
    const error = new Error(getErrorCode(payload));
    error.code = getErrorCode(payload);
    error.payload = payload;
    error.status = response.status;
    throw error;
  }

  return payload;
};
